import { useState, useEffect } from "react";
import axios from "axios";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, AlertCircle, Check } from "lucide-react";
import { API } from "@/config";
import { useAuth } from "@/components/AuthProvider";
import { toast } from "sonner";

/**
 * Public branded workspace page at /w/{slug}.
 * Three modes (chosen by URL state):
 *   1. ?invite=<token>   → invite-acceptance flow (handles mismatched-email gracefully)
 *   2. logged-out        → branded login/register, with brand color + logo
 *   3. logged-in member  → instant switch into the workspace, redirect to dashboard
 */
export default function PublicWorkspacePage() {
  const { slug } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const inviteToken = search.get("invite");
  const { user, login, register, logout } = useAuth();
  const [ws, setWs] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [acceptState, setAcceptState] = useState(null); // null | "accepting" | "ok" | {error}
  const [inviteEmail, setInviteEmail] = useState(null); // email this invite was sent to (best-effort)

  useEffect(() => {
    axios
      .get(`${API}/workspaces/by-slug/${slug}`)
      .then((r) => setWs(r.data))
      .catch((e) => setLoadError(e.response?.data?.detail || "Workspace not found"));
  }, [slug]);

  const acceptInvite = async () => {
    setAcceptState("accepting");
    try {
      const r = await axios.post(`${API}/workspaces/invites/accept`, { token: inviteToken });
      if (r.data.workspace_id) {
        await axios.post(`${API}/workspaces/switch/${r.data.workspace_id}`);
      }
      setAcceptState("ok");
      toast.success(`Welcome to ${ws?.branding?.brand_name || ws?.name}`);
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not accept the invitation.";
      // The backend returns the wrong-email message when account ≠ invited email.
      if (detail && detail.toLowerCase().includes("different email")) {
        // Best-effort: try to figure out who it was for via no-auth peek
        // (we don't have a pubic endpoint for invite metadata, so just message the user)
        setAcceptState({ error: detail, mismatch: true });
      } else {
        setAcceptState({ error: detail });
      }
    }
  };

  const switchAndGo = async () => {
    try {
      await axios.post(`${API}/workspaces/switch/${ws.id}`);
      toast.success(`Switched to ${ws.branding?.brand_name || ws.name}`);
      navigate("/dashboard");
    } catch (e) {
      toast.error("You're not a member of this workspace yet.");
      navigate("/dashboard");
    }
  };

  const submitAuth = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else await register(name, email, password);
      // After auth, the useEffect below re-runs because `user` changes.
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auth failed");
    }
    setBusy(false);
  };

  const signOutAndShowLogin = async () => {
    try { await logout(); } catch (e) { /* swallow */ }
    setEmail("");
    setPassword("");
    setMode("login");
    setAcceptState(null);
  };

  // ---------- RENDER ----------
  if (loadError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="glass max-w-md w-full">
          <CardContent className="p-8 text-center">
            <h1 className="text-2xl font-bold mb-2">Workspace not found</h1>
            <p className="text-muted-foreground mb-4">{loadError}</p>
            <Link to="/"><Button variant="outline" data-testid="public-ws-home-btn">Back to home</Button></Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!ws) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-violet-500" />
      </div>
    );
  }

  const brand = ws.branding || {};
  const accent = brand.primary_color || "#7C5CFC";
  const brandName = brand.brand_name || ws.name;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-6"
      style={{ background: "radial-gradient(ellipse at top, " + accent + "1a, transparent 60%), #0a0a0a" }}
      data-testid={`public-workspace-${slug}`}
    >
      <Helmet><title>{`${inviteToken ? "Accept invite" : "Sign in"} · ${brandName}`}</title></Helmet>
      <Card className="glass max-w-md w-full border-white/10">
        <CardContent className="p-8">
          {/* Brand header */}
          <div className="flex flex-col items-center text-center mb-6">
            {brand.logo_url ? (
              <img src={brand.logo_url} alt={brandName} className="w-16 h-16 rounded-xl mb-3 object-contain bg-white/5 p-2" />
            ) : (
              <div className="w-16 h-16 rounded-xl mb-3 flex items-center justify-center text-2xl font-bold" style={{ background: accent + "33", color: accent }}>
                {brandName.charAt(0).toUpperCase()}
              </div>
            )}
            <h1 className="text-2xl font-bold">{brandName}</h1>
          </div>

          {/* MODE A: invite token + logged in */}
          {inviteToken && user && acceptState === null && (
            <div className="space-y-4 text-center" data-testid="public-ws-accept-prompt">
              <p className="text-sm text-muted-foreground">
                You've been invited to join <strong>{brandName}</strong>. You're currently signed in as <strong>{user.email}</strong>.
              </p>
              <Button onClick={acceptInvite} className="w-full rounded-full" style={{ background: accent }} data-testid="public-ws-accept-btn">
                Accept invitation
              </Button>
              <button onClick={signOutAndShowLogin} className="text-xs text-muted-foreground hover:text-foreground underline" data-testid="public-ws-signout-link">
                Not you? Sign in with a different account
              </button>
            </div>
          )}

          {/* MODE B: invite token + accepting */}
          {inviteToken && acceptState === "accepting" && (
            <div className="text-center py-4">
              <Loader2 className="w-8 h-8 animate-spin text-violet-500 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Accepting invitation…</p>
            </div>
          )}

          {/* MODE C: invite token + accepted */}
          {inviteToken && acceptState === "ok" && (
            <div className="text-center py-4">
              <div className="w-12 h-12 rounded-full bg-teal-600/20 mx-auto mb-3 flex items-center justify-center">
                <Check className="w-6 h-6 text-teal-500" />
              </div>
              <p className="text-sm">You're in. Redirecting to your dashboard…</p>
            </div>
          )}

          {/* MODE D: invite token + email mismatch */}
          {inviteToken && acceptState && acceptState.error && acceptState.mismatch && (
            <div className="space-y-4" data-testid="public-ws-mismatch">
              <div className="flex gap-3 p-4 rounded-lg bg-amber-600/10 border border-amber-600/30">
                <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">This invite is for a different email.</p>
                  <p className="text-muted-foreground">You're signed in as <strong>{user?.email}</strong>, but this invitation was sent to a different address. Sign out and sign in with the email that received the invitation.</p>
                </div>
              </div>
              <Button onClick={signOutAndShowLogin} className="w-full rounded-full" style={{ background: accent }} data-testid="public-ws-signout-btn">
                Sign out and switch account
              </Button>
              <Link to="/dashboard" className="block text-center text-xs text-muted-foreground hover:text-foreground underline">
                Or just continue to my own dashboard →
              </Link>
            </div>
          )}

          {/* MODE E: invite token + generic error */}
          {inviteToken && acceptState && acceptState.error && !acceptState.mismatch && (
            <div className="space-y-4">
              <div className="flex gap-3 p-4 rounded-lg bg-violet-600/10 border border-violet-600/30">
                <AlertCircle className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-semibold mb-1">Couldn't accept the invitation</p>
                  <p className="text-muted-foreground">{acceptState.error}</p>
                </div>
              </div>
              <Link to="/dashboard"><Button variant="outline" className="w-full rounded-full">Continue to my dashboard</Button></Link>
            </div>
          )}

          {/* MODE F: no invite + logged in (member or not) */}
          {!inviteToken && user && (
            <div className="space-y-3 text-center">
              <p className="text-sm text-muted-foreground">Signed in as {user.email}.</p>
              <Button onClick={switchAndGo} className="w-full rounded-full" style={{ background: accent }} data-testid="public-ws-switch-btn">
                Open {brandName}
              </Button>
              <button onClick={signOutAndShowLogin} className="text-xs text-muted-foreground hover:text-foreground underline">
                Use a different account
              </button>
            </div>
          )}

          {/* MODE G: not logged in (with or without invite) → branded auth */}
          {!user && (
            <>
              <p className="text-sm text-muted-foreground text-center mb-4">
                {inviteToken ? "Sign in (or create an account) with the email that received the invitation." : (mode === "login" ? "Sign in to your workspace" : "Create your account")}
              </p>
              <form onSubmit={submitAuth} className="space-y-3">
                {mode === "register" && (
                  <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" required className="bg-white/5 border-white/10" data-testid="public-ws-name" />
                )}
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" required className="bg-white/5 border-white/10" data-testid="public-ws-email" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" required minLength={6} className="bg-white/5 border-white/10" data-testid="public-ws-password" />
                <Button type="submit" disabled={busy} className="w-full rounded-full" style={{ background: accent }} data-testid="public-ws-submit">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  {mode === "login" ? "Sign in" : "Create account"}
                </Button>
              </form>
              <div className="text-center text-sm text-muted-foreground mt-4">
                {mode === "login" ? (
                  <>New here? <button onClick={() => setMode("register")} className="text-foreground hover:underline" data-testid="public-ws-switch-register">Create an account</button></>
                ) : (
                  <>Already have an account? <button onClick={() => setMode("login")} className="text-foreground hover:underline" data-testid="public-ws-switch-login">Sign in</button></>
                )}
              </div>
            </>
          )}

          {brand.powered_by_enabled !== false && (
            <p className="text-center text-xs text-muted-foreground mt-6 opacity-60">Powered by Creatara</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
